import { useEffect, useRef, useState } from 'react';
import { IconBackspace, IconShieldLock, IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';

/**
 * ManagerApprovalModal — manager-PIN step-up modal.
 *
 * Phase 4.6 — redesigned to match the DayNotOpenOverlay aesthetic:
 * dark "system-lockout" panel, red urgency, visible filled-slot
 * animation per keypress, on-screen keypad with big tap targets.
 *
 * Owner directive: every privileged step-up looks the same — close-day
 * with variance, paid-out, void, refund, discount-over-cap, account
 * charge over limit. This is the ONE component every flow uses.
 *
 * Reads:   /api/auth/elevate (POST with PIN)
 * Writes:  (server-side) hp_elevation cookie + audit event
 */

interface Props {
  open: boolean;
  /** Plain-English description of the action being approved. */
  action: string;
  /** Optional helper / context line. */
  description?: string;
  onClose: () => void;
  /** Called only on successful elevation. */
  onApproved: () => void;
}

const ELEVATE_URL = '/api/auth/elevate';

export default function ManagerApprovalModal({
  open, action, description, onClose, onApproved,
}: Props) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const wasOpen = useRef(open);

  // Reset state on open
  useEffect(() => {
    if (open && !wasOpen.current) {
      setPin('');
      setError(null);
      setSubmitting(false);
      setSuccess(false);
      setShake(false);
    }
    wasOpen.current = open;
  }, [open]);

  // Keyboard handling — hardware keyboard support on iPad with a
  // keyboard case. Numeric keys append; Backspace removes; Escape cancels.
  useEffect(() => {
    if (!open || submitting || success) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        setError(null);
        setPin((p) => (p.length < 4 ? p + e.key : p));
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setPin((p) => p.slice(0, -1));
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, success, onClose]);

  const append = (d: string) => {
    if (submitting || success) return;
    setError(null);
    setPin((p) => (p.length < 4 ? p + d : p));
  };
  const back = () => {
    if (submitting || success) return;
    setPin((p) => p.slice(0, -1));
  };

  // Auto-submit on 4 digits
  useEffect(() => {
    if (!open || pin.length !== 4 || submitting || success) return;
    let cancelled = false;
    (async () => {
      setSubmitting(true);
      try {
        const r = await fetch(ELEVATE_URL, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error ?? 'Incorrect manager PIN');
        }
        if (cancelled) return;
        setSuccess(true);
        // Brief success flash before resolving so the cashier sees the tick.
        setTimeout(() => {
          if (!cancelled) {
            onApproved();
            onClose();
          }
        }, 350);
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message || 'Incorrect manager PIN');
        setShake(true);
        setTimeout(() => setShake(false), 400);
        setPin('');
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, open]);

  if (!open) return null;

  return (
    <>
      <style>{`
        @keyframes pinPop {
          0%   { transform: scale(0.6); opacity: 0.4; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes pinShake {
          0%, 100% { transform: translateX(0); }
          20%      { transform: translateX(-8px); }
          40%      { transform: translateX(8px); }
          60%      { transform: translateX(-6px); }
          80%      { transform: translateX(6px); }
        }
      `}</style>

      <div
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center px-6"
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md rounded-2xl overflow-hidden animate-slide-up bg-[#0A0A0A] border-2 border-[#1F1F1F] shadow-[0_25px_80px_-20px_rgba(221,0,0,0.4),0_8px_24px_rgba(0,0,0,0.6)]"
        >
          {/* Top accent stripe — red for "elevation required", flips green on success */}
          <div className={`h-1 ${success ? 'bg-gradient-to-r from-[#1B4D2A] via-[#2EBE5A] to-[#1B4D2A]' : 'bg-gradient-to-r from-[#7A0303] via-[#DD0000] to-[#7A0303]'}`} />

          <div className="p-9">
            {/* Icon */}
            <div className="relative w-16 h-16 mx-auto mb-5">
              <div className={`absolute inset-0 rounded-full blur-xl ${success ? 'bg-[#2EBE5A]/20' : 'bg-[#DD0000]/15'}`} />
              <div className={`relative w-16 h-16 rounded-2xl inline-flex items-center justify-center shadow-lg ${success ? 'bg-[#021A0B] border-2 border-[#2EBE5A]/40' : 'bg-[#1A0303] border-2 border-[#DD0000]/40'}`}>
                {success ? (
                  <IconCircleCheck size={34} stroke={2} className="text-[#3DDB6E]" />
                ) : (
                  <IconShieldLock size={30} stroke={2.25} className="text-[#FF3333]" />
                )}
              </div>
            </div>

            {/* Eyebrow */}
            <p className={`inline-flex items-center gap-2 text-12 font-bold uppercase tracking-[0.3em] mb-3 ${success ? 'text-[#A8E2BC]' : 'text-[#FF4444]'} w-full justify-center`}>
              {!success && <IconAlertTriangle size={14} stroke={2.5} fill="currentColor" className="text-[#FF4444]" />}
              {success ? 'Approved' : 'Manager approval required'}
            </p>

            {/* Headline + description */}
            <h2 className="text-20 font-bold text-[#FFFFFF] leading-tight text-center mb-2">
              {action}
            </h2>
            {description && (
              <p className="text-13 font-medium text-[#B8BCC2] leading-relaxed text-center mb-6 max-w-[340px] mx-auto">
                {description}
              </p>
            )}

            {/* PIN slots — big, visible, animated */}
            <div
              className={`flex items-center justify-center gap-3 mb-5 ${shake ? '' : ''}`}
              style={shake ? { animation: 'pinShake 0.4s ease-in-out' } : undefined}
              aria-label="Manager PIN"
            >
              {[0, 1, 2, 3].map((i) => {
                const filled = i < pin.length;
                return (
                  <div
                    key={i}
                    className={[
                      'w-14 h-16 rounded-lg border-2 inline-flex items-center justify-center transition-colors',
                      filled
                        ? (success ? 'bg-[#021A0B] border-[#2EBE5A]' : 'bg-[#1A0303] border-[#FF3333]')
                        : 'bg-[#15151A] border-[#2A2A30]',
                    ].join(' ')}
                  >
                    {filled && (
                      <span
                        className={`w-4 h-4 rounded-full ${success ? 'bg-[#3DDB6E]' : 'bg-[#FF3333]'} shadow-[0_0_12px_currentColor]`}
                        style={{ animation: 'pinPop 0.18s ease-out' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Error */}
            {error && (
              <p className="text-13 font-bold text-[#FF4444] text-center mb-4 uppercase tracking-wider" role="alert">
                {error}
              </p>
            )}

            {/* Keypad — dark themed, big tap targets (60×60+) */}
            <div className="grid grid-cols-3 gap-2.5">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <KeypadButton key={n} onClick={() => append(String(n))} disabled={submitting || success}>{n}</KeypadButton>
              ))}
              <button
                type="button"
                onClick={onClose}
                disabled={submitting || success}
                className="h-15 min-h-[60px] inline-flex items-center justify-center rounded-lg text-12 font-bold uppercase tracking-widest text-[#7A7E84] hover:bg-[#15151A] hover:text-[#B8BCC2] disabled:opacity-30 transition-colors"
              >
                Cancel
              </button>
              <KeypadButton onClick={() => append('0')} disabled={submitting || success}>0</KeypadButton>
              <button
                type="button"
                onClick={back}
                disabled={submitting || success}
                aria-label="Backspace"
                className="h-15 min-h-[60px] inline-flex items-center justify-center rounded-lg text-[#7A7E84] hover:bg-[#15151A] hover:text-[#FF3333] disabled:opacity-30 transition-colors"
              >
                <IconBackspace size={24} stroke={2} />
              </button>
            </div>

            <p className="text-11 font-semibold text-[#5A5E64] text-center mt-5 uppercase tracking-widest">
              {submitting ? 'Verifying…' : success ? 'Granted' : 'Manager: enter your 4-digit PIN'}
            </p>
          </div>

          {/* Bottom stripe — matches top */}
          <div className={`h-1 ${success ? 'bg-gradient-to-r from-[#1B4D2A] via-[#2EBE5A] to-[#1B4D2A]' : 'bg-gradient-to-r from-[#7A0303] via-[#DD0000] to-[#7A0303]'}`} />
        </div>
      </div>
    </>
  );
}

function KeypadButton({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="h-15 min-h-[60px] inline-flex items-center justify-center rounded-lg bg-[#15151A] border-2 border-[#2A2A30] text-22 font-bold text-[#FFFFFF] hover:bg-[#1F1F26] hover:border-[#FF3333] active:scale-95 disabled:opacity-30 transition-all tabular-nums"
    >
      {children}
    </button>
  );
}
